import { jsx as _jsx } from "react/jsx-runtime";
import originalI81next from 'i18next';
import { useContext, useEffect, forwardRef, } from 'react';
import { I18nextProvider, I18nContext } from 'react-i18next';
import { useI18n } from './hook.js';
import { i18nLibraryInstance } from './library.js';
/* @__NO_SIDE_EFFECTS__ */
export const withI18n = (WrappedComponent) => {
    const ComponentWithI18n = forwardRef(({ currentLanguage, ...props }, ref) => {
        const { i18n, isInitialized } = useI18n(currentLanguage);
        // Detect if parent I18nextProvider exists using context directly
        // (useTranslation() can suspend, so we use useContext instead)
        const i18nContext = useContext(I18nContext);
        const parentI18n = i18nContext?.i18n;
        const hasParentProvider = parentI18n?.isInitialized &&
            parentI18n !== i18nLibraryInstance &&
            parentI18n !== originalI81next;
        // @ts-expect-error Custom flag set by initializeI18n(), not in i18next types
        const hasGlobalInstance = originalI81next?.__serviceI18nGlobalInstance;
        const shouldSkipProvider = hasParentProvider || hasGlobalInstance;
        useEffect(() => {
            if (currentLanguage && isInitialized) {
                i18n.changeLanguage(currentLanguage);
            }
        }, [currentLanguage, isInitialized, i18n]);
        // Always wait for initialization—rendering before useEffect sets isInitialized=true
        // can cause components to mount before providers are ready.
        if (!isInitialized) {
            if (import.meta.env.MODE === 'test') {
                return (
                // eslint-disable-next-line i18next/no-literal-string
                _jsx("span", { "data-testid": "service-i18n-not-initialized", children: "Service-i18n is not initialized yet. Please ensure the test waits for the initialization to complete." }));
            }
            return null;
        }
        if (shouldSkipProvider) {
            return _jsx(WrappedComponent, { ref: ref, ...props });
        }
        return (_jsx(I18nextProvider, { i18n: i18n, children: _jsx(WrappedComponent, { ref: ref, ...props }) }));
    });
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ComponentWithI18n.displayName = `withI18n(${displayName})`;
    return ComponentWithI18n;
};
